import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { it } from '../../services/api';
import StatCard from '../../components/common/StatCard';
import Card from '../../components/common/Card';
import Badge from '../../components/common/Badge';
import { theme } from '../../theme';

const HEALTH_COLORS = {
  Healthy: '#10B981',
  Monitor: '#F59E0B',
  'At Risk': '#F97316',
  Critical: '#EF4444',
};

const STATUS_COLORS = {
  available: theme.success,
  assigned: theme.primary,
  in_repair: theme.warning,
  retired: theme.textMuted,
  lost: theme.danger,
  disposed: '#6B7280',
};

const PRIORITY_COLORS = ['#10B981', '#F59E0B', '#EF4444', '#9D174D'];

function ITDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    it.getITDashboard()
      .then((r) => { setStats(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: '3rem', color: theme.textMuted }}>Loading...</div>;
  if (!stats) return <p style={{ color: theme.danger }}>Failed to load dashboard.</p>;

  const sc = stats.status_counts || {};
  const hd = stats.health_distribution || {};
  const tc = stats.ticket_counts || {};
  const obp = stats.open_by_priority || {};

  const healthData = Object.entries(hd).map(([name, value]) => ({ name, value }));
  const statusData = Object.entries(sc).map(([name, value]) => ({ name, value })).filter((x) => x.value > 0);
  const priorityData = [
    { name: 'Low', count: obp.low || 0 },
    { name: 'Medium', count: obp.medium || 0 },
    { name: 'High', count: obp.high || 0 },
    { name: 'Critical', count: obp.critical || 0 },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>IT Dashboard</h1>
        <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>Fleet health, tickets, and license overview</p>
      </div>

      {/* Top stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
        <StatCard icon="💻" label="Total Assets" value={stats.total_assets} color={theme.primary} />
        <StatCard icon="✅" label="Available" value={sc.available || 0} color={theme.success} />
        <StatCard icon="🔗" label="Assigned" value={sc.assigned || 0} color={theme.info} />
        <StatCard icon="🔧" label="In Repair" value={sc.in_repair || 0} color={theme.warning} />
        <StatCard icon="🎫" label="Open Tickets" value={(tc.open || 0) + (tc.in_progress || 0)} color={theme.danger} />
      </div>

      {/* Charts row 1 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        <Card title="Asset Health Distribution">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={healthData} cx="50%" cy="50%" outerRadius={85} dataKey="value"
                label={({ name, percent }) => percent > 0 ? `${name} ${(percent * 100).toFixed(0)}%` : null}
                labelLine={false}>
                {healthData.map((entry) => <Cell key={entry.name} fill={HEALTH_COLORS[entry.name] || theme.primary} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
          {/* Health score legend summary */}
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap', marginTop: '0.5rem' }}>
            {Object.entries(hd).map(([label, count]) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.78rem' }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: HEALTH_COLORS[label], display: 'inline-block' }} />
                <span style={{ color: theme.textMuted }}>{label}:</span>
                <strong style={{ color: HEALTH_COLORS[label] }}>{count}</strong>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Open Tickets by Priority">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={priorityData} barSize={36}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: theme.textMuted }} />
              <YAxis tick={{ fontSize: 11, fill: theme.textMuted }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: theme.white, border: `1px solid ${theme.border}`, borderRadius: '0.5rem' }} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]} name="Tickets">
                {priorityData.map((_, i) => <Cell key={i} fill={PRIORITY_COLORS[i]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '0.5rem', fontSize: '0.8rem', color: theme.textMuted }}>
            <span>Resolved: <strong style={{ color: theme.success }}>{tc.resolved || 0}</strong></span>
            <span>Closed: <strong style={{ color: theme.textMuted }}>{tc.closed || 0}</strong></span>
            <span>Escalated: <strong style={{ color: theme.danger }}>{tc.escalated || 0}</strong></span>
          </div>
        </Card>
      </div>

      {/* Charts row 2 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        <Card title="Assets by Category">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={stats.category_breakdown || []} layout="vertical" barSize={18}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.border} horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: theme.textMuted }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: theme.textMuted }} width={90} />
              <Tooltip contentStyle={{ background: theme.white, border: `1px solid ${theme.border}`, borderRadius: '0.5rem' }} />
              <Bar dataKey="count" fill={theme.primary} radius={[0, 4, 4, 0]} name="Assets" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Asset Status Breakdown">
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={statusData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value"
                label={({ name, value }) => `${name} (${value})`} labelLine={false}>
                {statusData.map((entry) => <Cell key={entry.name} fill={STATUS_COLORS[entry.name] || theme.primary} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Alerts row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        {/* Expiring warranties */}
        <Card title={`Expiring Warranties — Next 90 Days (${(stats.expiring_warranties || []).length})`}>
          {(stats.expiring_warranties || []).length === 0 ? (
            <p style={{ color: theme.success, textAlign: 'center', padding: '2rem 0', margin: 0 }}>✅ No warranties expiring soon</p>
          ) : (stats.expiring_warranties || []).map((a, i) => (
            <div key={i} onClick={() => navigate(`/it/assets/${a.id}`)}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.625rem 0', borderBottom: `1px solid ${theme.border}`, cursor: 'pointer' }}>
              <div>
                <p style={{ fontWeight: 600, fontSize: '0.875rem', margin: 0, color: theme.text }}>{a.name}</p>
                <p style={{ fontSize: '0.72rem', color: theme.textMuted, margin: '0.15rem 0 0' }}>{a.asset_code} · {new Date(a.warranty_expiry).toLocaleDateString()}</p>
              </div>
              <span style={{ fontSize: '0.8rem', fontWeight: 700, color: a.days_remaining <= 30 ? theme.danger : theme.warning, flexShrink: 0, marginLeft: '0.5rem' }}>
                {a.days_remaining === 0 ? 'Today' : `${a.days_remaining}d`}
              </span>
            </div>
          ))}
        </Card>

        {/* Recent open tickets */}
        <Card title="Recent Open Tickets">
          {(stats.recent_tickets || []).length === 0 ? (
            <p style={{ color: theme.success, textAlign: 'center', padding: '2rem 0', margin: 0 }}>✅ No open tickets</p>
          ) : (stats.recent_tickets || []).map((t, i) => (
            <div key={i} onClick={() => navigate(`/it/tickets/${t.id}`)}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.625rem 0', borderBottom: i < stats.recent_tickets.length - 1 ? `1px solid ${theme.border}` : 'none', cursor: 'pointer' }}>
              <div>
                <p style={{ fontWeight: 600, fontSize: '0.875rem', margin: 0, color: theme.text }}>{t.title}</p>
                <p style={{ fontSize: '0.72rem', color: theme.textMuted, margin: '0.15rem 0 0' }}>{t.ticket_code} · {t.raised_by_name}</p>
              </div>
              <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0, marginLeft: '0.5rem' }}>
                <Badge status={t.priority} />
                <Badge status={t.status} />
              </div>
            </div>
          ))}
        </Card>

        {/* License expiry */}
        {(stats.expiring_licenses || []).length > 0 && (
          <Card title={`License Expiry Alerts (${stats.expiring_licenses.length})`} style={{ gridColumn: 'span 2' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.75rem' }}>
              {stats.expiring_licenses.map((l, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.625rem 0.875rem', background: theme.pageBg, borderRadius: '0.5rem', border: `1px solid ${theme.border}` }}>
                  <div>
                    <p style={{ fontWeight: 600, fontSize: '0.875rem', margin: 0 }}>{l.name}</p>
                    <p style={{ fontSize: '0.72rem', color: theme.textMuted, margin: '0.15rem 0 0' }}>Expires: {new Date(l.expiry_date).toLocaleDateString()}</p>
                  </div>
                  <span style={{ fontSize: '0.85rem', fontWeight: 700, color: l.days_remaining <= 7 ? theme.danger : theme.warning }}>{l.days_remaining}d</span>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

export default ITDashboard;
